import { RESERVED_SLUGS } from "./reserved-slugs";

export type TenantSurface = "public_web" | "staff_dashboard" | "member_portal";

export type HostResolution =
  | { kind: "platform"; slug: string; surface: TenantSurface }
  | { kind: "central"; app: "control_plane" | "app_entry" | "staff_door" | "login" }
  | { kind: "custom"; hostname: string }
  | { kind: "invalid"; reason: string };

export const TENANT_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/;
const SURFACE_LABEL: Readonly<Record<string, TenantSurface>> = {
  dash: "staff_dashboard",
  members: "member_portal",
};

/** First path segments on the staff door that are never a church (spec section 4; `auth` for the hand-off, step 2). */
export const DOOR_PATHS: readonly string[] = ["sign-in", "choose", "me", "t", "auth"];

/** Where a hand-off lands on any HoW app (spec section 6). */
export const HANDOFF_PATH = "/auth/continue";
const HANDOFF_CODE = /^[0-9a-f]{64}$/;

/** Lowercases, strips a port suffix and a trailing dot. */
export function normalizeHostname(input: string): string {
  return input.trim().toLowerCase().replace(/:\d+$/, "").replace(/\.$/, "");
}

export function isValidTenantSlug(slug: string): boolean {
  return TENANT_SLUG_PATTERN.test(slug) && !RESERVED_SLUGS.includes(slug);
}

/**
 * Classifies a request hostname. Custom domains still require a database lookup
 * (public.resolve_tenant_host); this only decides which lookup path applies.
 */
export function parseHost(input: string, baseDomain: string): HostResolution {
  const host = normalizeHostname(input);
  const base = normalizeHostname(baseDomain);
  if (host.length === 0) return { kind: "invalid", reason: "empty hostname" };
  if (host === base) return { kind: "central", app: "app_entry" };
  if (!host.endsWith(`.${base}`)) return { kind: "custom", hostname: host };
  const labels = host.slice(0, -(base.length + 1)).split(".");
  if (labels.length === 1) {
    const label = labels[0] ?? "";
    if (label === "admin") return { kind: "central", app: "control_plane" };
    if (label === "dash") return { kind: "central", app: "staff_door" };
    if (label === "login" || label === "app") return { kind: "central", app: "login" };
    if (!isValidTenantSlug(label))
      return { kind: "invalid", reason: "reserved or malformed tenant label" };
    return { kind: "platform", slug: label, surface: "public_web" };
  }
  if (labels.length === 2) {
    const slug = labels[0] ?? "";
    const surface = SURFACE_LABEL[labels[1] ?? ""];
    if (surface === undefined || !isValidTenantSlug(slug)) {
      return { kind: "invalid", reason: "unknown surface or malformed tenant label" };
    }
    return { kind: "platform", slug, surface };
  }
  return { kind: "invalid", reason: "too many labels under the platform domain" };
}

/** Builds the platform fallback host for a tenant surface, e.g. zoe.dash.how.dcxlogic.com. */
export function buildPlatformHost(
  slug: string,
  surface: TenantSurface,
  baseDomain: string,
): string {
  const base = normalizeHostname(baseDomain);
  if (surface === "public_web") return `${slug}.${base}`;
  return `${slug}.${surface === "staff_dashboard" ? "dash" : "members"}.${base}`;
}

/** The church named by a staff-door path (`/alpha/people` → alpha); null for a door path or anything else. */
export function churchFromPath(pathname: string): { slug: string; rest: string } | null {
  const match = /^\/([^/]+)(\/.*)?$/.exec(pathname);
  if (match === null) return null;
  const slug = match[1] ?? "";
  if (DOOR_PATHS.includes(slug) || !isValidTenantSlug(slug)) return null;
  const rest = match[2] ?? "/";
  return { slug, rest: rest === "" ? "/" : rest };
}

/** https://dash.<base>/<slug><rest>, or the door itself without a slug. */
export function staffDoorUrl(baseDomain: string, slug?: string, rest = "/"): string {
  const base = normalizeHostname(baseDomain);
  return slug === undefined ? `https://dash.${base}/` : `https://dash.${base}/${slug}${rest}`;
}

/**
 * The query with `moved=1` appended (spec section 9: the sign-in page says why the person signs in again). The
 * existing query is kept byte for byte, so nothing it carries is re-encoded.
 */
function withMoved(search: string): string {
  const query = search.startsWith("?") ? search.slice(1) : search;
  if (/(^|&)moved=1(&|$)/.test(query)) return `?${query}`;
  return query === "" ? "?moved=1" : `?${query}&moved=1`;
}

/**
 * Where an old address forwards (spec section 9): a church's old dashboard host keeps path, query and hash under
 * /<slug>; login.<base> and app.<base> go to the door's sign-in keeping query and hash. Both add `moved=1` to the
 * query so the sign-in page can say why. Null when nothing forwards.
 */
export function legacyStaffForward(
  resolution: HostResolution,
  baseDomain: string,
  loc: { pathname: string; search: string; hash: string },
): string | null {
  const base = normalizeHostname(baseDomain);
  if (resolution.kind === "platform" && resolution.surface === "staff_dashboard") {
    const path = loc.pathname === "" ? "/" : loc.pathname;
    return `https://dash.${base}/${resolution.slug}${path}${withMoved(loc.search)}${loc.hash}`;
  }
  if (resolution.kind === "central" && resolution.app === "login") {
    return `https://dash.${base}/sign-in${withMoved(loc.search)}${loc.hash}`;
  }
  return null;
}

/** https://<target>/auth/continue#handoff=<code>: the code in the fragment, which no server or Referer sees (ruling R2). */
export function handoffUrl(targetHost: string, code: string): string {
  const host = normalizeHostname(targetHost);
  if (!/^[a-z0-9.-]+$/.test(host)) throw new Error("handoffUrl: malformed target host");
  if (!HANDOFF_CODE.test(code)) throw new Error("handoffUrl: malformed hand-off code");
  return `https://${host}${HANDOFF_PATH}#handoff=${code}`;
}

/** The code from `location.hash`, or null when absent or malformed. */
export function readHandoffCode(hash: string): string | null {
  if (!hash.startsWith("#")) return null;
  const value = new URLSearchParams(hash.slice(1)).get("handoff");
  return value !== null && HANDOFF_CODE.test(value) ? value : null;
}

/** A "." or ".." path segment, written plainly or percent-encoded (%2e). */
const DOT_SEGMENT = /^(\.|%2e){1,2}$/i;

/**
 * A page on the same site: starts with one "/", printable ASCII only, no backslash, no "." or ".." segment (plain or
 * %2e) before the query or fragment, at most 512 characters. Mirrors private.is_safe_next_path, which is
 * authoritative. Never decode `next`: it is checked and used exactly as written.
 */
export function isSafeNextPath(path: string): boolean {
  const pathPart = path.split(/[?#]/, 1)[0] ?? "";
  return (
    !pathPart.split("/").some((segment) => DOT_SEGMENT.test(segment)) &&
    path.length >= 1 &&
    path.length <= 512 &&
    path.startsWith("/") &&
    !path.startsWith("//") &&
    !path.includes("\\") &&
    /^[\x21-\x7e]+$/.test(path)
  );
}

/** A church's own domain (dash.mychurch.com): any host outside the platform domain. */
export function isChurchOwnHost(hostname: string, baseDomain: string): boolean {
  return parseHost(hostname, baseDomain).kind === "custom";
}
